import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { FiPlus, FiZap, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { applySkinTone, getTwemojiUrl } from '../utils/emojiUtils';

const EmojiItem = ({ emoji, skinTone, onSingleCopy, onAddToTray, lang, disableAnimation = false }) => {
  const charWithTone = applySkinTone(emoji, skinTone);
  const isFlag = emoji.category === 'flags';
  const isNew = emoji.year && emoji.year >= 2024;
  const isHot = emoji.hot === true;
  
  const displayName = lang === 'zh-TW' ? (emoji.nameZh || emoji.name) : emoji.name;

  const content = (
    <>
      <div className="relative z-10 flex items-center justify-center w-full h-full pointer-events-none select-none">
        {isFlag ? (
          <img 
            src={getTwemojiUrl(emoji.char)} 
            alt={emoji.name} 
            className="w-[70%] h-[70%] object-contain drop-shadow-sm"
            loading="lazy"
          />
        ) : (
          <span className="drop-shadow-sm transform translate-y-[2px]">{charWithTone}</span>
        )}
      </div>
      
      {/* 修正：將 Badge 移至左下角，避開視覺中心 */}
      {(isHot || isNew) && (
        <div className="absolute bottom-1.5 left-1.5 z-20 pointer-events-none">
          <div className={`
            flex items-center gap-1 text-[8px] md:text-[9px] font-black text-white 
            px-2 py-0.5 rounded-full shadow-sm backdrop-blur-[2px]
            ${isHot ? 'bg-amber-500/90' : 'bg-indigo-600/90'}
            transition-all group-hover:scale-90 group-hover:opacity-70
          `}>
            <SafeIcon icon={isHot ? FiStar : FiZap} className="text-[7px] md:text-[8px] fill-current" />
            <span className="leading-none">{isHot ? 'HOT' : emoji.year}</span>
          </div>
        </div>
      )}

      {/* 手機版與電腦版通用的 Plus 按鈕優化 */}
      <button 
        onClick={(e) => { e.stopPropagation(); onAddToTray(emoji, charWithTone); }}
        className="
          absolute top-1.5 right-1.5 w-8 h-8 md:w-7 md:h-7 
          bg-indigo-500/90 hover:bg-indigo-600 text-white 
          rounded-full flex items-center justify-center shadow-lg 
          md:opacity-0 md:group-hover:opacity-100 transition-all z-30 
          active:scale-125 backdrop-blur-sm
        "
      >
        <SafeIcon icon={FiPlus} className="text-sm stroke-[3]" />
      </button>
    </>
  );

  const baseClass = `
    group w-full aspect-square flex items-center justify-center 
    text-3xl md:text-4xl bg-white rounded-2xl md:rounded-[1.5rem] 
    shadow-sm border border-slate-100 transition-all duration-300 
    active:scale-95 font-emoji overflow-hidden relative 
    hover:shadow-xl hover:border-indigo-200 hover:-translate-y-1
    ${isHot ? 'bg-gradient-to-br from-white to-amber-50/30' : ''}
  `;

  return (
    <div className="relative">
      {disableAnimation ? (
        <button 
          onClick={() => onSingleCopy(emoji, charWithTone)} 
          className={baseClass}
          title={displayName}
        >
          {content}
        </button>
      ) : (
        <motion.button 
          initial={{ opacity: 0, scale: 0.9 }} 
          animate={{ opacity: 1, scale: 1 }}
          onClick={() => onSingleCopy(emoji, charWithTone)} 
          className={baseClass}
          title={displayName}
        >
          {content}
        </motion.button>
      )}
    </div>
  );
};

export default memo(EmojiItem);